import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { aggregateDelays, KPIS } from '../shared/criticalPath';
import { isAdminOrGestor } from '../lib/rbac';
import { desvioLabel } from '../components/ui';

export function Atrasos() {
  const { currentOrgId, areas, grants } = useAuth();
  const { loading, summaries } = useOrgData(currentOrgId);
  const nav = useNavigate();

  const areaLabel = useMemo(() => {
    const m = new Map(areas.map((a) => [a.key, a.name]));
    return (k: string) => m.get(k) ?? k;
  }, [areas]);

  // Admin/gestor veem o processo todo; um executor vê no "por etapa" apenas as
  // etapas das suas áreas (o "por responsável" abaixo continua sendo do time).
  const isPmo = grants ? isAdminOrGestor(grants) : false;
  const kpis = useMemo(() => KPIS(summaries), [summaries]);
  const { byArea, byStage } = useMemo(
    () => aggregateDelays(summaries, isPmo ? undefined : (grants?.areaKeys ?? [])),
    [summaries, isPmo, grants],
  );

  const [openStage, setOpenStage] = useState<Set<number>>(new Set());
  const toggleStage = (n: number) =>
    setOpenStage((s) => {
      const next = new Set(s);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  const maxArea = Math.max(1, ...byArea.map((a) => a.totalDelayDays));
  const maxStage = Math.max(1, ...byStage.map((a) => a.totalDelayDays));

  const topProducts = useMemo(
    () =>
      [...summaries]
        .filter((s) => s.desvioDias > 0)
        .sort((a, b) => b.desvioDias - a.desvioDias)
        .slice(0, 8),
    [summaries],
  );

  return (
    <>
      <div className="topbar">
        <h1>Painel de atrasos</h1>
        <div className="stat-strip">
          <div className={`stat-chip ${kpis.atrasado ? 'warn' : ''}`}>
            <span className="n tabular">{kpis.atrasado}</span>
            <span className="l">Atrasados</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.atencao}</span>
            <span className="l">Atenção</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{kpis.noPrazo}</span>
            <span className="l">No prazo</span>
          </div>
        </div>
      </div>

      <div className="content">
        {loading ? (
          <div className="center-note">Carregando…</div>
        ) : (
          <>
            <div className="card card-pad">
              <div className="section-title">
                <h2>Atraso por responsável</h2>
                <span className="muted">quem está segurando o quê, agora</span>
              </div>
              {byArea.length === 0 && <p className="muted">Nenhuma etapa atrasada. 🎉</p>}
              {byArea.map((a) => (
                <div className="bar-row" key={a.areaKey}>
                  <div>{areaLabel(a.areaKey)}</div>
                  <div className="track">
                    <i style={{ width: `${(a.totalDelayDays / maxArea) * 100}%` }} />
                  </div>
                  <div className="n tabular">
                    {a.lateStages} etapa(s) · {Math.round(a.totalDelayDays)} d
                  </div>
                </div>
              ))}
            </div>

            <div className="card card-pad">
              <div className="section-title">
                <h2>Atraso por etapa do processo</h2>
                <span className="muted">
                  {isPmo
                    ? 'quais etapas travam em várias linhas — clique para ver os projetos'
                    : 'suas etapas em atraso — clique para ver os projetos'}
                </span>
              </div>
              {byStage.length === 0 && (
                <p className="muted">
                  {isPmo ? 'Nenhuma etapa atrasada. 🎉' : 'Você não tem etapas em atraso. 🎉'}
                </p>
              )}
              {byStage.map((s) => {
                const open = openStage.has(s.n);
                return (
                  <div className="stage-delay" key={s.n}>
                    <div
                      className="bar-row bar-row-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleStage(s.n)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleStage(s.n)}
                    >
                      <div>
                        <span className="exp-caret">{open ? '▾' : '▸'}</span>{' '}
                        <span className="muted tabular">{s.n}.</span> {s.name}
                      </div>
                      <div className="track">
                        <i style={{ width: `${(s.totalDelayDays / maxStage) * 100}%` }} />
                      </div>
                      <div className="n tabular">
                        {s.lateProducts} linha(s) · {Math.round(s.totalDelayDays)} d
                      </div>
                    </div>
                    {open && (
                      <div className="stage-delay-list">
                        {s.items.map((it) => (
                          <div className="stage-delay-item" key={it.productId}>
                            <button
                              className="linklike"
                              onClick={() => nav(`/produto/${it.productId}`)}
                              title="Abrir o projeto para atualizar esta etapa"
                            >
                              {it.brand} · {it.name}
                            </button>
                            <span className="pd-late">vencida há {it.atrasoDias} d</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="card">
              <div className="card-pad section-title">
                <h2>Linhas mais atrasadas</h2>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Marca</th>
                      <th>Linha</th>
                      <th>Fase atual</th>
                      <th>Desvio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.map((s) => (
                      <tr
                        key={s.productId}
                        className="clickable"
                        onClick={() => nav(`/produto/${s.productId}`)}
                      >
                        <td className="muted">{s.brand}</td>
                        <td style={{ fontWeight: 600 }}>{s.name}</td>
                        <td>{s.faseAtual}</td>
                        <td className="tabular" style={{ color: 'var(--st-atrasada)' }}>
                          {desvioLabel(s.desvioDias)}
                        </td>
                      </tr>
                    ))}
                    {topProducts.length === 0 && (
                      <tr>
                        <td colSpan={4} className="center-note">
                          Nenhuma linha atrasada. Tudo dentro do prazo. 🎉
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
